import { Container, Graphics, Sprite, Text, Texture } from 'pixi.js';
import gsap from 'gsap';
import { GAME_HEIGHT, GAME_WIDTH, TUNING, type Side } from '../config/tuning';
import { isMobile } from '../config/mobile';

interface SidePanel {
  root: Container;
  nameText: Text;
  totalText: Text;
  dragText: Text;
  atRiskText: Text;
  lungFrame: Sprite;
  lungFill: Sprite;
  lungTrack: Graphics;
  lungThresholdMark: Graphics;
  chokeFrame: Sprite;
  chokeFill: Sprite;
  chokeTrack: Graphics;
  multiplierText: Text;
  lungPulseTween: gsap.core.Tween | gsap.core.Timeline | null;
  chokePulseTween: gsap.core.Tween | gsap.core.Timeline | null;
  multiplierPulseTween: gsap.core.Tween | gsap.core.Timeline | null;
  thresholdPulseTween: gsap.core.Tween | gsap.core.Timeline | null;
  currentLungPct: number;
  currentChokePct: number;
  qteArmedBanked: boolean;
  lungBarW: number;
  chokeBarW: number;
}

const makeSprite = (key: string, x: number, y: number, anchor: [number, number] = [0, 0]): Sprite => {
  const s = new Sprite(Texture.from(key));
  s.anchor.set(anchor[0], anchor[1]);
  s.position.set(x, y);
  return s;
};

const setSpriteSize = (sprite: Sprite, width: number, height: number): void => {
  const tex = sprite.texture;
  if (!tex || tex.width <= 0 || tex.height <= 0) return;
  sprite.scale.set(width / tex.width, height / tex.height);
};

/**
 * Hud — per-side "player card" layout. Each card groups name + total + drag/at-risk
 * + lung bar + choke bar into a single Container that lives at the top edge of the
 * screen. No overlapping bars, everything fits inside the card bounds.
 */
export class Hud {
  readonly container: Container;
  private panels: Record<Side, SidePanel>;
  private turnArrow: Sprite;
  private turnArrowBob: gsap.core.Tween | null = null;
  private comboRing: Sprite;
  private comboRingSpin: gsap.core.Tween | null = null;
  private jointBar: Graphics;
  private mobile: boolean;

  // QTE overlay
  private qte: Container;
  private qteFrame: Sprite;
  private qteTrack: Sprite;
  private qteGolden: Sprite;
  private qteLine: Sprite;
  private qteWinText: Text;
  private qteBarW: number;
  private qteBarH: number;
  private qteBarX: number;
  private qteBarY: number;
  private qteGoldenBaseScale = 1;
  private qteActiveTweens: (gsap.core.Tween | gsap.core.Timeline)[] = [];

  constructor() {
    this.mobile = isMobile();
    this.container = new Container();
    this.container.sortableChildren = true;
    this.container.zIndex = 85;

    // Score cards hug the top corners with a uniform margin.
    const cardW = 340;
    const cardMargin = 24;
    const cardY = 24;

    this.panels = {
      player: this.createPanel('YOU', 0x5fdbcf, cardMargin, cardY, cardW),
      ai: this.createPanel('BOT', 0xff9ab5, GAME_WIDTH - cardW - cardMargin, cardY, cardW),
    };

    // Turn arrow — hovers just above the characters' heads, centered.
    const arrowY = GAME_HEIGHT * 0.42;
    this.turnArrow = makeSprite('hud_turn_arrow', GAME_WIDTH / 2, arrowY, [0.5, 0.5]);
    this.turnArrow.visible = false;
    this.turnArrow.zIndex = 100;
    this.container.addChild(this.turnArrow);

    // Combo ring — centered in the joint-pass zone, bottom third.
    const ringY = GAME_HEIGHT - 160;
    this.comboRing = makeSprite('hud_combo_ring', GAME_WIDTH / 2, ringY, [0.5, 0.5]);
    this.comboRing.visible = false;
    this.comboRing.zIndex = 100;
    this.comboRing.scale.set(0.45);
    this.container.addChild(this.comboRing);

    // Joint bar — top center, between the two player cards
    this.jointBar = new Graphics();
    this.jointBar.zIndex = 100;
    this.container.addChild(this.jointBar);
    this.drawJointBar(100);

    // QTE overlay setup
    this.qte = new Container();
    this.qte.sortableChildren = true;
    this.qte.zIndex = 120;
    this.qte.visible = false;
    this.qte.alpha = 0;

    this.qteBarW = TUNING.qte.barWidthDesktop;
    this.qteBarH = TUNING.qte.barHeightDesktop;
    const qteY = TUNING.qte.yDesktop;
    this.qteBarX = GAME_WIDTH / 2 - this.qteBarW / 2;
    this.qteBarY = qteY - this.qteBarH / 2;

    const frameScale = this.qteBarW / 400;
    this.qteFrame = makeSprite('hud_qte_frame', GAME_WIDTH / 2, qteY, [0.5, 0.5]);
    this.qteFrame.scale.set(frameScale);
    this.qteFrame.zIndex = 1;

    this.qteTrack = makeSprite('hud_qte_track', this.qteBarX, this.qteBarY);
    setSpriteSize(this.qteTrack, this.qteBarW, this.qteBarH);
    this.qteTrack.zIndex = 2;

    const goldenWidth = this.qteBarW * (TUNING.qte.goldenZoneWidthPct / 100);
    const goldenHeight = this.qteBarH + 14;
    this.qteGolden = makeSprite('hud_qte_golden', GAME_WIDTH / 2, qteY, [0.5, 0.5]);
    setSpriteSize(this.qteGolden, goldenWidth, goldenHeight);
    this.qteGoldenBaseScale = this.qteGolden.scale.x;
    this.qteGolden.zIndex = 3;

    const lineHeight = this.qteBarH + 22;
    this.qteLine = makeSprite('hud_qte_line', this.qteBarX + this.qteBarW, qteY, [0.5, 0.5]);
    setSpriteSize(this.qteLine, 12, lineHeight);
    this.qteLine.zIndex = 5;

    this.qteWinText = new Text({
      text: '',
      style: {
        fontFamily: 'Impact, sans-serif',
        fontSize: this.mobile ? 32 : 46,
        fill: 0xffd23f,
        stroke: { color: 0x1a0d22, width: 5 },
      },
    });
    this.qteWinText.anchor.set(0.5);
    this.qteWinText.position.set(GAME_WIDTH / 2, qteY - this.qteBarH - (this.mobile ? 18 : 26));
    this.qteWinText.alpha = 0;
    this.qteWinText.zIndex = 6;

    this.qte.addChild(this.qteFrame, this.qteTrack, this.qteGolden, this.qteLine, this.qteWinText);
    this.container.addChild(this.qte);
  }

  private createPanel(
    label: string,
    accentColor: number,
    x: number,
    y: number,
    cardW: number
  ): SidePanel {
    const mobile = this.mobile;
    const root = new Container();
    root.position.set(x, y);
    root.zIndex = 85;
    this.container.addChild(root);

    // Card dimensions (local coords inside root)
    const cardH = mobile ? 170 : 200;
    const padX = mobile ? 14 : 18;
    const padTop = mobile ? 10 : 14;

    // Background panel
    const bg = new Graphics()
      .roundRect(0, 0, cardW, cardH, 16)
      .fill({ color: 0x1a1226, alpha: 0.85 })
      .stroke({ color: accentColor, width: 2.5, alpha: 0.9 });
    bg.zIndex = 1;
    root.addChild(bg);

    // Accent strip at top
    const accent = new Graphics()
      .roundRect(padX, padTop, cardW - padX * 2, 3, 1.5)
      .fill({ color: accentColor, alpha: 0.85 });
    accent.zIndex = 2;
    root.addChild(accent);

    // ── Name label (top-left) ────────────────────────────────────────────────
    const nameText = new Text({
      text: label,
      style: {
        fontFamily: 'Impact, sans-serif',
        fontSize: mobile ? 18 : 22,
        fill: accentColor,
        letterSpacing: 2,
      },
    });
    nameText.position.set(padX, padTop + 10);
    nameText.zIndex = 3;
    root.addChild(nameText);

    // ── Total score (top, large) ─────────────────────────────────────────────
    const totalText = new Text({
      text: '0.0s',
      style: {
        fontFamily: 'Impact, sans-serif',
        fontSize: mobile ? 30 : 40,
        fill: 0xffffff,
        stroke: { color: 0x1a0d22, width: 3 },
      },
    });
    totalText.anchor.set(1, 0);
    totalText.position.set(cardW - padX, padTop + 6);
    totalText.zIndex = 3;
    root.addChild(totalText);

    // ── Drag + At risk row (below name/total) ────────────────────────────────
    const subRowY = mobile ? 52 : 64;
    const dragText = new Text({
      text: 'Drag: 0.00s',
      style: {
        fontFamily: 'Inter, sans-serif',
        fontSize: mobile ? 12 : 14,
        fill: 0x88e8db,
      },
    });
    dragText.position.set(padX, subRowY);
    dragText.zIndex = 3;
    root.addChild(dragText);

    const atRiskText = new Text({
      text: 'At risk: 0.00s',
      style: {
        fontFamily: 'Inter, sans-serif',
        fontSize: mobile ? 12 : 14,
        fill: 0x5a605a,
      },
    });
    atRiskText.anchor.set(1, 0);
    atRiskText.position.set(cardW - padX, subRowY);
    atRiskText.zIndex = 3;
    root.addChild(atRiskText);

    // ── Lung bar (full width of card interior) ───────────────────────────────
    const lungBarW = cardW - padX * 2;
    const lungBarH = mobile ? 28 : 32;
    const lungBarY = subRowY + (mobile ? 22 : 26);

    const lungTrack = new Graphics()
      .roundRect(padX, lungBarY, lungBarW, lungBarH, lungBarH / 2)
      .fill({ color: 0x0a060f, alpha: 0.85 });
    lungTrack.zIndex = 4;
    root.addChild(lungTrack);

    const lungFill = makeSprite('hud_lung_fill_mask', padX, lungBarY);
    setSpriteSize(lungFill, lungBarW, lungBarH);
    lungFill.zIndex = 5;
    root.addChild(lungFill);

    // 75% QTE eligibility mark
    const markX = padX + lungBarW * (TUNING.qte.eligibilityLungPct / 100);
    const markH = lungBarH + (mobile ? 6 : 10);
    const markY = lungBarY - (mobile ? 3 : 5);
    const lungThresholdMark = new Graphics()
      .roundRect(markX - 1.5, markY, 3, markH, 1.5)
      .fill({ color: 0xffd23f, alpha: 0.9 });
    lungThresholdMark.zIndex = 7;
    root.addChild(lungThresholdMark);

    const lungFrame = makeSprite('hud_lung_frame', padX, lungBarY);
    setSpriteSize(lungFrame, lungBarW, lungBarH);
    lungFrame.zIndex = 6;
    root.addChild(lungFrame);

    // ── Choke bar (full width, below lung) ───────────────────────────────────
    const chokeBarW = cardW - padX * 2;
    const chokeBarH = mobile ? 14 : 16;
    const chokeBarY = lungBarY + lungBarH + (mobile ? 10 : 14);

    const chokeTrack = new Graphics()
      .roundRect(padX, chokeBarY, chokeBarW, chokeBarH, chokeBarH / 2)
      .fill({ color: 0x0a060f, alpha: 0.85 });
    chokeTrack.zIndex = 4;
    root.addChild(chokeTrack);

    const chokeFill = makeSprite('hud_choke_fill_mask', padX, chokeBarY);
    setSpriteSize(chokeFill, 0, chokeBarH);
    chokeFill.zIndex = 5;
    root.addChild(chokeFill);

    const chokeFrame = makeSprite('hud_choke_frame', padX, chokeBarY);
    setSpriteSize(chokeFrame, chokeBarW, chokeBarH);
    chokeFrame.zIndex = 6;
    root.addChild(chokeFrame);

    // ── Multiplier badge (top-right, floats outside right edge) ─────────────
    const multiplierText = new Text({
      text: '',
      style: {
        fontFamily: 'Impact, sans-serif',
        fontSize: mobile ? 30 : 38,
        fill: 0xffd23f,
        stroke: { color: 0x0f0a14, width: 4 },
      },
    });
    multiplierText.anchor.set(0.5);
    multiplierText.position.set(cardW - padX - 16, padTop + (mobile ? 50 : 58));
    multiplierText.alpha = 0;
    multiplierText.zIndex = 8;
    root.addChild(multiplierText);

    return {
      root,
      nameText,
      totalText,
      dragText,
      atRiskText,
      lungFrame,
      lungFill,
      lungTrack,
      lungThresholdMark,
      chokeFrame,
      chokeFill,
      chokeTrack,
      multiplierText,
      lungPulseTween: null,
      chokePulseTween: null,
      multiplierPulseTween: null,
      thresholdPulseTween: null,
      currentLungPct: 100,
      currentChokePct: 0,
      qteArmedBanked: false,
      lungBarW,
      chokeBarW,
    };
  }

  setLung(side: Side, pct: number): void {
    const p = this.panels[side];
    const clamped = Math.max(0, Math.min(100, pct));
    p.currentLungPct = clamped;
    const ratio = clamped / 100;
    const lungBarH = this.mobile ? 28 : 32;
    setSpriteSize(p.lungFill, p.lungBarW * ratio, lungBarH);
    p.lungFill.tint = Hud.lungTintForPct(clamped);
    this.updateLungWarning(side);
    this.updateLungThresholdMark(side);
  }

  setQteArmedHint(side: Side, armed: boolean): void {
    this.panels[side].qteArmedBanked = armed;
    this.updateLungThresholdMark(side);
  }

  private updateLungThresholdMark(side: Side): void {
    const p = this.panels[side];
    const eligible = p.currentLungPct >= TUNING.qte.eligibilityLungPct || p.qteArmedBanked;
    if (eligible && !p.thresholdPulseTween) {
      p.thresholdPulseTween = gsap.to(p.lungThresholdMark, {
        alpha: 0.55,
        duration: 0.75,
        yoyo: true,
        repeat: -1,
        ease: 'sine.inOut',
      });
    } else if (!eligible && p.thresholdPulseTween) {
      p.thresholdPulseTween.kill();
      p.thresholdPulseTween = null;
      p.lungThresholdMark.alpha = 0.35;
    } else if (!eligible && !p.thresholdPulseTween) {
      p.lungThresholdMark.alpha = 0.35;
    }
  }

  setChoke(side: Side, pct: number): void {
    const p = this.panels[side];
    const clamped = Math.max(0, Math.min(100, pct));
    p.currentChokePct = clamped;
    const ratio = clamped / 100;
    const chokeBarH = this.mobile ? 14 : 16;
    setSpriteSize(p.chokeFill, p.chokeBarW * ratio, chokeBarH);
    this.updateChokeWarning(side);
  }

  private static lungTintForPct(pct: number): number {
    if (pct >= 50) return 0xffffff;
    if (pct >= 25) return 0xffe07a;
    if (pct >= 12) return 0xff9a3c;
    return 0xff3838;
  }

  private updateLungWarning(side: Side): void {
    const p = this.panels[side];
    const pct = p.currentLungPct;

    const shouldHeavy = pct < 12;
    const shouldMild = pct < 25 && pct >= 12;
    const shouldNone = pct >= 25;

    if (shouldHeavy && p.lungPulseTween?.vars.data === 'heavy') return;
    if (shouldMild && p.lungPulseTween?.vars.data === 'mild') return;
    if (shouldNone && !p.lungPulseTween) return;

    if (p.lungPulseTween) {
      p.lungPulseTween.kill();
      p.lungPulseTween = null;
      p.lungFrame.alpha = 1;
      p.lungFill.alpha = 1;
    }

    if (shouldHeavy) {
      p.lungPulseTween = gsap.to([p.lungFrame, p.lungFill], {
        alpha: 0.55,
        duration: 0.22,
        yoyo: true,
        repeat: -1,
        ease: 'sine.inOut',
        data: 'heavy',
      });
    } else if (shouldMild) {
      p.lungPulseTween = gsap.to([p.lungFrame], {
        alpha: 0.7,
        duration: 0.42,
        yoyo: true,
        repeat: -1,
        ease: 'sine.inOut',
        data: 'mild',
      });
    }
  }

  private updateChokeWarning(side: Side): void {
    const p = this.panels[side];
    const pct = p.currentChokePct;

    const t = TUNING.choke.threshold;
    const isHeavy = pct >= t.heavyWarning;
    const isMild = pct >= t.mildWarning && pct < t.heavyWarning;
    const isNone = pct < t.mildWarning;

    if (isHeavy && p.chokePulseTween?.vars.data === 'heavy') return;
    if (isMild && p.chokePulseTween?.vars.data === 'mild') return;
    if (isNone && !p.chokePulseTween) return;

    if (p.chokePulseTween) {
      p.chokePulseTween.kill();
      p.chokePulseTween = null;
      p.chokeFrame.alpha = 1;
    }

    if (isHeavy) {
      p.chokePulseTween = gsap.to(p.chokeFrame, {
        alpha: 0.5,
        duration: 0.18,
        yoyo: true,
        repeat: -1,
        ease: 'sine.inOut',
        data: 'heavy',
      });
    } else if (isMild) {
      p.chokePulseTween = gsap.to(p.chokeFrame, {
        alpha: 0.7,
        duration: 0.32,
        yoyo: true,
        repeat: -1,
        ease: 'sine.inOut',
        data: 'mild',
      });
    }
  }

  setTotal(side: Side, seconds: number): void {
    this.panels[side].totalText.text = `${seconds.toFixed(1)}s`;
  }

  setCurrentDrag(side: Side, seconds: number): void {
    this.panels[side].dragText.text = `Drag: ${seconds.toFixed(2)}s`;
  }

  setAtRisk(side: Side, seconds: number): void {
    const p = this.panels[side];
    p.atRiskText.text = `At risk: ${seconds.toFixed(2)}s`;
    p.atRiskText.style.fill = seconds > 0 ? 0xffaa55 : 0x5a605a;
  }

  setMultiplier(side: Side, count: number): void {
    const p = this.panels[side];
    const otherSide = side === 'player' ? 'ai' : 'player';
    this.panels[otherSide].multiplierText.alpha = 0;

    if (count <= 0) {
      p.multiplierText.alpha = 0;
      return;
    }

    const prevText = p.multiplierText.text;
    const newText = `x${count}`;
    p.multiplierText.text = newText;
    p.multiplierText.alpha = 1;

    if (prevText !== newText) {
      if (p.multiplierPulseTween) p.multiplierPulseTween.kill();
      p.multiplierPulseTween = gsap.fromTo(
        p.multiplierText.scale,
        { x: 1, y: 1 },
        { x: 1.4, y: 1.4, duration: 0.15, yoyo: true, repeat: 1, ease: 'back.out' }
      );
    }
  }

  setTurnIndicator(side: Side | null): void {
    gsap.killTweensOf(this.turnArrow);
    gsap.killTweensOf(this.turnArrow.position);
    if (this.turnArrowBob) { this.turnArrowBob.kill(); this.turnArrowBob = null; }

    if (side !== 'player') {
      gsap.to(this.turnArrow, {
        alpha: 0,
        duration: 0.2,
        onComplete: () => { this.turnArrow.visible = false; },
      });
      return;
    }
    const arrowY = this.mobile ? 380 : 320;
    this.turnArrow.visible = true;
    this.turnArrow.alpha = 0;
    this.turnArrow.position.set(GAME_WIDTH / 2, arrowY);
    gsap.to(this.turnArrow, { alpha: 1, duration: 0.3 });
    this.turnArrowBob = gsap.to(this.turnArrow.position, {
      y: arrowY - 12,
      duration: 0.5,
      yoyo: true,
      repeat: -1,
      ease: 'sine.inOut',
    });
  }

  showComboRing(durationMs: number): void {
    if (this.comboRingSpin) this.comboRingSpin.kill();
    gsap.killTweensOf(this.comboRing);
    gsap.killTweensOf(this.comboRing.scale);

    this.comboRing.visible = true;
    this.comboRing.alpha = 0;
    const startScale = this.mobile ? 0.25 : 0.35;
    const endScale = this.mobile ? 0.35 : 0.45;
    this.comboRing.scale.set(startScale);

    gsap.to(this.comboRing, { alpha: 1, duration: 0.2, ease: 'back.out' });
    gsap.to(this.comboRing.scale, { x: endScale, y: endScale, duration: 0.2, ease: 'back.out' });
    this.comboRingSpin = gsap.to(this.comboRing, {
      rotation: Math.PI * 2,
      duration: durationMs / 1000,
      ease: 'none',
    });
  }

  hideComboRing(): void {
    const hideScale = this.mobile ? 0.45 : 0.55;
    gsap.to(this.comboRing, {
      alpha: 0,
      duration: 0.2,
      onComplete: () => { this.comboRing.visible = false; this.comboRing.rotation = 0; },
    });
    gsap.to(this.comboRing.scale, { x: hideScale, y: hideScale, duration: 0.2 });
    if (this.comboRingSpin) { this.comboRingSpin.kill(); this.comboRingSpin = null; }
  }

  // ── QTE overlay ─────────────────────────────────────────────────────────

  showQte(goldenStartPct: number, goldenEndPct: number): void {
    this.killQteTweens();
    const goldenCenter = (goldenStartPct + goldenEndPct) / 2;
    const cx = this.qteBarX + this.qteBarW * goldenCenter;
    this.qteGolden.position.set(cx, this.qteBarY + this.qteBarH / 2);

    this.qteLine.position.set(this.qteBarX + this.qteBarW, this.qteBarY + this.qteBarH / 2);
    this.qteLine.alpha = 1;
    this.qteWinText.text = '';
    this.qteWinText.alpha = 0;

    this.qte.visible = true;
    this.qte.alpha = 0;
    this.qte.scale.set(0.85);
    this.qteActiveTweens.push(
      gsap.to(this.qte, { alpha: 1, duration: 0.16, ease: 'power2.out' }),
      gsap.to(this.qte.scale, { x: 1, y: 1, duration: 0.22, ease: 'back.out(2)' }),
      gsap.to(this.qteGolden.scale, {
        x: this.qteGoldenBaseScale * 1.08,
        y: this.qteGoldenBaseScale * 1.08,
        duration: 0.55,
        yoyo: true,
        repeat: -1,
        ease: 'sine.inOut',
      }),
    );
  }

  updateQteLine(linePct: number): void {
    if (!this.qte.visible) return;
    const clamped = Math.max(0, Math.min(1, linePct));
    this.qteLine.x = this.qteBarX + this.qteBarW * clamped;
  }

  flashQteWin(pctReduced: number): void {
    this.killQteTweens(true);
    this.qteWinText.text = `-${pctReduced}% CHOKE`;
    this.qteWinText.alpha = 0;
    this.qteWinText.scale.set(0.6);
    this.qteWinText.y = this.qteBarY - (this.mobile ? 18 : 26);

    this.qteActiveTweens.push(
      gsap.fromTo(
        this.qteGolden.scale,
        { x: this.qteGoldenBaseScale * 1.15, y: this.qteGoldenBaseScale * 1.15 },
        {
          x: this.qteGoldenBaseScale * 1.4,
          y: this.qteGoldenBaseScale * 1.4,
          duration: 0.18,
          yoyo: true,
          repeat: 1,
          ease: 'power2.out',
        },
      ),
      gsap.to(this.qteWinText, { alpha: 1, duration: 0.12 }),
      gsap.to(this.qteWinText.scale, { x: 1.2, y: 1.2, duration: 0.25, ease: 'back.out(2.5)' }),
      gsap.to(this.qteWinText, {
        y: this.qteBarY - (this.mobile ? 60 : 80),
        alpha: 0,
        duration: 0.9,
        delay: 0.25,
        ease: 'power2.in',
      }),
      gsap.to(this.qte, {
        alpha: 0,
        duration: 0.45,
        delay: 0.55,
        onComplete: () => {
          this.qte.visible = false;
          this.qteWinText.text = '';
        },
      }),
    );
  }

  hideQte(): void {
    if (!this.qte.visible) return;
    this.killQteTweens(true);
    this.qteActiveTweens.push(
      gsap.to(this.qteLine, { alpha: 0, duration: 0.12 }),
      gsap.to(this.qte, {
        alpha: 0,
        duration: 0.22,
        onComplete: () => {
          this.qte.visible = false;
        },
      }),
    );
  }

  private killQteTweens(keepVisible = false): void {
    this.qteActiveTweens.forEach((t) => t.kill());
    this.qteActiveTweens = [];
    if (!keepVisible) {
      this.qte.alpha = 0;
      this.qte.visible = false;
    }
    this.qteGolden.scale.set(this.qteGoldenBaseScale);
  }

  drawJointBar(pct: number): void {
    const cx = GAME_WIDTH / 2;
    const mobile = this.mobile;
    const y = mobile ? 30 : 40;
    const totalW = mobile ? 420 : 640;
    const h = mobile ? 18 : 24;
    const cardboardW = totalW * (TUNING.joint.cardboardZonePct / 100);
    const ratio = Math.max(0, Math.min(100, pct)) / 100;
    const r = mobile ? 9 : 12;

    const g = this.jointBar;
    g.clear();

    g.roundRect(cx - totalW / 2, y, totalW, h, r).fill({ color: 0x0a060f, alpha: 0.7 });

    const fillW = totalW * ratio;
    const safeW = Math.max(0, fillW - cardboardW);
    const cardboardFillW = Math.max(0, fillW - safeW);

    if (safeW > 0) {
      g.roundRect(cx - totalW / 2 + cardboardW, y + 2, safeW - 2, h - 4, mobile ? 7 : 10)
        .fill({ color: 0xffd23f });
    }
    if (cardboardFillW > 0) {
      g.roundRect(cx - totalW / 2 + 2, y + 2, cardboardFillW - 2, h - 4, mobile ? 7 : 10)
        .fill({ color: 0xff3838 });
    }

    g.roundRect(cx - totalW / 2, y, totalW, h, r).stroke({ color: 0xfce0bd, width: 3, alpha: 0.9 });
    g.moveTo(cx - totalW / 2 + cardboardW, y)
      .lineTo(cx - totalW / 2 + cardboardW, y + h)
      .stroke({ color: 0xff6b35, width: 2 });
  }

  destroy(): void {
    if (this.turnArrowBob) this.turnArrowBob.kill();
    if (this.comboRingSpin) this.comboRingSpin.kill();
    this.qteActiveTweens.forEach((t) => t.kill());
    this.qteActiveTweens = [];
    Object.values(this.panels).forEach((p) => {
      p.lungPulseTween?.kill();
      p.chokePulseTween?.kill();
      p.multiplierPulseTween?.kill();
      p.thresholdPulseTween?.kill();
    });
    this.container.destroy({ children: true });
  }
}
